import {
	Flex,
	FormNote,
	Label,
	LayoutBox,
	Select,
} from '@bamboohr/fabric';
import {
	useState,
	useMemo,
} from 'react';

import { OptionalFieldHelp } from '../optional-field-help.react';
import type { Option, OptionHelp } from '../withholding.react/types';

type OptionalSelectProp = {
	id: string;
	data: {
		label: string,
		options: Option[]
	};
	help: OptionHelp;
	isUnemploymentField: boolean;
	required?: boolean;
	viewOnly: boolean;
}

export function OptionalSelect({
	id,
	data,
	help,
	isUnemploymentField,
	required = false,
	viewOnly,
}: OptionalSelectProp) {
	// find the selected value if the data provides one
	const selectedOption = useMemo(() => {
		return data.options.find(option => option.selected === true)
	}, [data]) || data.options[0];

	const [selectedValue, setSelectedValue] = useState(selectedOption?.val || null);

	// TODO: abstract this to the parent level to keep this component "dumb"
	const namePrefix = isUnemploymentField ? 'uiState' : 'state';

	const isClearable = required;

	const items = useMemo(() => {
		const options = data.options.map((option) => {
			return {
				text: option.displayText,
				value: option.val,
			};
		});
		return options
	}, [data]);
	
	const handleSelectChange = (selectedValues: Array<string>) => {
		const [selectedValue = null] = selectedValues;
		setSelectedValue(selectedValue);
	};

	return (
		// On submitting tax withholdings, we manually assign CSS error classes to elements rendered by this component.
		// This is because we don't have a state engine for the form, which is still largely managed in jQuery.
		// So we assign a key prop to the selected value here in order to force a re-rendering each time a new value is selected.
		<LayoutBox key={ selectedValue }>
			<Label
				htmlFor={ `${ namePrefix }[optional][${ id }]` }
			>
				{ data.label }{required ? '*': ''}
			</Label>
			<Flex
				alignItems="center"
				gap={ 1 }
			>
				<Select
					isClearable={ isClearable }
					isDisabled={ viewOnly }
					items={ items }
					name={ `${ namePrefix }[optional][${ id }]` }
					onChange={ handleSelectChange }
					placeholder={required ? `- ${ $.__('Select') } -` : null}
					required={ required }
					selectedValues={ [selectedValue] }
					width={ 7 }
				/>
				<FormNote>
					<OptionalFieldHelp helpData={ help } />
				</FormNote>
			</Flex>
		</LayoutBox>
	);
}